import React, { FC, useEffect, useState } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Box,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { getRolesTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyDentistTemplate } from 'apollo/templates/user/mutation.graphql';
import { Organization } from 'models/organization';
import { ClinicDropdown, Dentist, Role } from 'models/user';
import AuthMethodFields, {
    authMethodVariables,
    validateAuthMethodFields,
} from 'components/users/shared/auth-method-fields/auth-method-fields.component';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { userIdVar } from 'apollo/reactive-vars';
import ChildClinicsAutocomplete from '../add-dentist-dialog/child-clinics-autocomplete.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { nameRegExp } from 'config/regex';

type UpdateDentistFormProps = {
    onDialogClose: () => void;
    dentistToUpdate: Dentist;
    onUserUpdated: () => void;
};
const UpdateDentistForm: FC<UpdateDentistFormProps> = ({
    onDialogClose,
    dentistToUpdate,
    onUserUpdated,
}: UpdateDentistFormProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'organization']);
    const currentUser = currentUserVar();
    const [showUserTitleInput, setShowUserTitleInput] = useState(false);
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const currentOrganization = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyDentist] = useMutation(modifyDentistTemplate, {
        onCompleted: () => {
            onUserUpdated();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { roles } = {}, loading: rolesLoading } = useQuery(getRolesTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { type: 'DENTIST' },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: Dentist) => {
        const errors: Record<string, string> = {};
        if (showUserTitleInput && !values.title) {
            errors.title = translate('users:VALIDATION:TITLE');
        }
        if (!values.firstName) {
            errors.firstName = translate('users:VALIDATION:FIRST_NAME');
        }
        if (values.firstName && !isFirstNameValid) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.lastName) {
            errors.lastName = translate('users:VALIDATION:LAST_NAME');
        }
        if (values.lastName && !isLastNameValid) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        if (!values.role) {
            errors.role = translate('users:VALIDATION:ROLE');
        }
        validateAuthMethodFields(values, translate, errors, {
            currentMethod: dentistToUpdate.authMethod,
        });
        if (!values.defaultClinic) {
            errors.defaultClinic = translate('organization:VALIDATION:DEFAULT_CLINIC');
        }
        return errors;
    };
    const formik = useFormik({
        initialValues: {
            _id: dentistToUpdate._id,
            title: dentistToUpdate.title,
            firstName: dentistToUpdate.firstName,
            lastName: dentistToUpdate.lastName,
            email: dentistToUpdate.email,
            role: dentistToUpdate.role,
            authMethod: dentistToUpdate.authMethod,
            syncid: dentistToUpdate.syncid,
            personalIdentityNumber: dentistToUpdate.personalIdentityNumber ?? '',
            organization: dentistToUpdate.organization as Organization,
            defaultClinic: {
                _id: (dentistToUpdate.defaultClinic as Organization)?._id,
                name: (dentistToUpdate.defaultClinic as Organization)?.name,
            },
            isActive: dentistToUpdate.isActive,
            enableUserSync: dentistToUpdate.enableUserSync || false,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const dentist = {
                _id: values._id,
                name: { firstName: values.firstName, lastName: values.lastName },
                email: values.email,
                role: values.role,
                ...authMethodVariables(values),
                organization: (currentUser.organization as Organization)?._id,
                defaultClinic: (values.defaultClinic as ClinicDropdown)?._id,
                active: values.isActive,
                ...(showUserTitleInput ? { title: values.title } : {}),
            };

            await modifyDentist({ variables: { dentist } });
            onDialogClose();
        },
    });

    useEffect(() => {
        const canEditUserTitle = customerSettings.getCustomerSetting(
            currentOrganization.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.EDIT_USER_TITLE,
        );
        setShowUserTitleInput(canEditUserTitle);
        formik.validateForm();
        setIsFirstNameValid(nameRegExp.test(dentistToUpdate.firstName || ''));
        setIsLastNameValid(nameRegExp.test(dentistToUpdate.lastName || ''));
    }, [dentistToUpdate]);

    const handleChildClinicChange = (clinic?: ClinicDropdown | null) => {
        formik.setFieldValue('defaultClinic', clinic);
    };

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        setIsFirstNameValid(nameRegExp.test(firstName));
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        setIsLastNameValid(nameRegExp.test(lastName));
        formik.setFieldValue('lastName', lastName);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Box mt={1}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="first-name"
                                    label={translate('common:FIRST_NAME')}
                                    margin="dense"
                                    required
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    onChange={(e) => handleFirstNameChange(e.target.value)}
                                    onBlur={formik.getFieldProps('firstName').onBlur}
                                    name={formik.getFieldProps('firstName').name}
                                    value={formik.getFieldProps('firstName').value}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="last-name"
                                    label={translate('common:LAST_NAME')}
                                    margin="dense"
                                    required
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    onChange={(e) => handleLastNameChange(e.target.value)}
                                    onBlur={formik.getFieldProps('lastName').onBlur}
                                    name={formik.getFieldProps('lastName').name}
                                    value={formik.getFieldProps('lastName').value}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="email"
                                    label={translate('common:EMAIL')}
                                    margin="dense"
                                    required
                                    type="email"
                                    disabled
                                    helperText={formik.touched.email && formik.errors.email}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    {...formik.getFieldProps('email')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            {rolesLoading ? undefined : (
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    error={formik.touched.role && Boolean(formik.errors.role)}
                                    required
                                >
                                    <InputLabel id="role-select-label">{translate('common:ROLE')}</InputLabel>
                                    <Select
                                        labelId="role-select-label"
                                        id="role-select"
                                        label={translate('common:ROLE')}
                                        {...formik.getFieldProps('role')}
                                    >
                                        {roles.map((role: Role) => (
                                            <MenuItem key={role._id} value={role.role}>
                                                {translate(`common:${role.role}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText error={formik.touched.role && Boolean(formik.errors.role)}>
                                        {formik.touched.role && formik.errors.role}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        </Grid>
                        <AuthMethodFields formik={formik} currentMethod={dentistToUpdate.authMethod} />
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <ChildClinicsAutocomplete
                                    onChildClinicChange={handleChildClinicChange}
                                    {...formik.getFieldProps('defaultClinic')}
                                    parentClinic={formik.values.organization as Organization}
                                    helperText={formik.touched.defaultClinic && formik.errors.defaultClinic}
                                    error={formik.touched.defaultClinic && Boolean(formik.errors.defaultClinic)}
                                    fullWidth
                                    useInForm
                                    required
                                />
                            </FormControl>
                        </Grid>
                        {showUserTitleInput ? (
                            <Grid item xs={6}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="title"
                                        label={translate('common:TITLE')}
                                        required
                                        helperText={formik.touched.title && formik.errors.title}
                                        error={formik.touched.title && Boolean(formik.errors.title)}
                                        {...formik.getFieldProps('title')}
                                    />
                                </FormControl>
                            </Grid>
                        ) : undefined}
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <FormControlLabel
                                    id="isActive"
                                    name="isActive"
                                    control={
                                        <Switch
                                            color="secondary"
                                            checked={formik.values.isActive}
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label={
                                        formik.values.isActive
                                            ? translate('common:ACTIVE')
                                            : translate('common:INACTIVE')
                                    }
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>
                <Button
                    disabled={!formik.isValid || userIdVar() === dentistToUpdate._id}
                    variant="contained"
                    type="submit"
                >
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default UpdateDentistForm;
